/**
 * The small, repeated pieces every Tranche screen needs: page headers, stat
 * tiles, empty/error states, the standing disclaimers, a numeric table.
 *
 * These are compositions of the Wanted primitives — not parallel primitives.
 * They exist so the COPY.md sentences and the token classes are written once.
 */

import { Icon, type IconName } from "@/assets/icons/Icon";
import { Alert, Button, Card, Spinner, Tag, TextButton, Tooltip } from "@/components/ui";
import { useStockNames } from "@/hooks/api";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { errorAction, errorMessage } from "@/lib/api/client";
import { PERFORMANCE_DISCLAIMER } from "@/lib/disclaimers";
import { formatDecimal, formatMoney, formatRate, signTone, withSign } from "@/lib/format";
import { toast } from "@/stores/toastStore";
import type { Decimal } from "@/types/api";
import type { KeyboardEvent, ReactNode } from "react";
import { Link } from "react-router-dom";

// ------------------------------------------------------------------ headings

export function PageHeader({
  title,
  description,
  actions,
  back,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  back?: { label: string; to: string };
}) {
  useDocumentTitle(title);
  /*
    리듬은 규칙 하나예요: 뒤로가기 → (8px) → 제목 → (4px) → 설명, 그리고 헤더 아래는 항상
    32px. 예전에는 `mb-6` 고정에 내부 간격이 `gap-3` 이라, `actions`·`back` 이 있는 화면과
    없는 화면의 "헤더 끝 ~ 첫 카드" 거리가 서로 달라 보였어요.
  */
  return (
    <header className="mb-8 flex flex-col gap-2">
      {back ? (
        <Link
          to={back.to}
          className="-ml-1 inline-flex w-fit items-center gap-1 rounded-sm px-1 py-0.5 text-label-2 text-label-alternative transition-colors duration-fast ease-standard hover:bg-fill-alternative hover:text-label-normal"
        >
          <Icon name="chevronLeft" size={14} />
          {back.label}
        </Link>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="wds-title-2 text-label-strong">{title}</h1>
          {description ? (
            <p className="max-w-2xl text-label-1 text-label-alternative">{description}</p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
    </header>
  );
}

export function Section({
  title,
  description,
  actions,
  children,
  aside,
}: {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    /*
      3단 리듬: 머리(제목·설명, 하단 hairline) / 본문(가장 넉넉함) / 꼬리(바탕 한 단 낮춤).
      예전에는 셋 다 `px-5 py-4` 라서 표가 든 섹션과 두 줄짜리 섹션이 똑같이 생겼고,
      머리와 본문의 경계도 hairline 하나뿐이라 눈이 어디서 끊어 읽을지 몰랐어요.
    */
    <Card variant="outline" padding={0} radius="var(--radius-lg)">
      {title ? (
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b border-line-alternative px-6 pb-4 pt-5">
          <div className="flex min-w-0 flex-col gap-1">
            <h2 className="wds-headline-1 text-label-strong">{title}</h2>
            {description ? (
              <p className="text-label-2 text-label-alternative">{description}</p>
            ) : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className="px-6 py-5">{children}</div>
      {aside ? (
        <div className="rounded-b-lg border-t border-line-alternative bg-background-alternative px-6 py-3">
          {aside}
        </div>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------- stat tiles

export function StatTile({
  label,
  value,
  sub,
  tone = "neutral",
  hint,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "neutral" | "positive" | "negative";
  hint?: string;
}) {
  const valueTone =
    tone === "positive"
      ? "text-status-positive"
      : tone === "negative"
        ? "text-status-negative"
        : "text-label-strong";
  /*
    방향은 색으로만 말하지 않아요 — 캐럿을 같이 답니다. 색약 사용자에게 붉은 값과 초록 값이
    같은 회색으로 보이는 자리이고, 이 타일이 화면에서 가장 먼저 읽히는 숫자예요.

    단 **부호가 붙은 값에만** 답니다. `tone` 은 호출부마다 뜻이 달라요 — 손익에서는 "올랐다·
    내렸다" 지만 `/admin` 의 "보호 없는 포지션"·"실행 중 규칙" 에서는 "이건 문제다" 예요.
    거기에 하향 캐럿을 달면 2건이 줄었다는 뜻이 되어 없는 사실을 그립니다. 부호는 손익 쪽만
    `withSign` 으로 붙이므로, 부호 유무가 곧 "이 숫자에 방향이 있는가" 입니다.
  */
  const signed = value.startsWith("+") || value.startsWith("-");
  const caret = !signed
    ? null
    : tone === "positive"
      ? "caretUp"
      : tone === "negative"
        ? "caretDown"
        : null;
  return (
    <Card variant="outline" padding={20} radius="var(--radius-lg)">
      <div className="flex items-center gap-1">
        <span className="text-caption-1 text-label-alternative">{label}</span>
        {hint ? (
          <Tooltip label={hint}>
            <span className="inline-flex text-label-alternative transition-colors duration-fast ease-standard hover:text-label-normal">
              <Icon name="circleInfo" size={14} />
            </span>
          </Tooltip>
        ) : null}
      </div>
      <p className={`mt-1.5 flex items-baseline gap-1 ${valueTone}`}>
        {caret ? (
          <span className="inline-flex shrink-0 self-center">
            <Icon name={caret} size={16} />
          </span>
        ) : null}
        <span className="tabular wds-title-3">{value}</span>
      </p>
      {sub ? <p className="tabular mt-1 text-caption-1 text-label-assistive">{sub}</p> : null}
    </Card>
  );
}

/** A signed P&L value painted with the semantic status token. */
export function PnlText({
  amount,
  rate,
  currency,
  size = "body",
}: {
  amount?: Decimal;
  rate?: Decimal;
  /** 주면 통화 기호까지 붙여요. 달러는 소수 둘째 자리가 살아야 해요 — 정수로 반올림하면
   *  $12.34 손익이 "12" 로 보여요. 안 주면 예전처럼 단위 없는 정수예요. */
  currency?: "KRW" | "USD";
  size?: "body" | "label";
}) {
  const basis = amount ?? rate ?? "0";
  const tone = signTone(basis);
  const color =
    tone === "positive"
      ? "text-status-positive"
      : tone === "negative"
        ? "text-status-negative"
        : "text-label-neutral";
  const type = size === "body" ? "text-body-2" : "text-label-2";
  const parts: string[] = [];
  if (amount !== undefined)
    parts.push(withSign(currency ? formatMoney(amount, currency) : formatDecimal(amount, 0)));
  if (rate !== undefined) parts.push(`(${withSign(formatRate(rate))})`);
  return <span className={`tabular ${type} ${color}`}>{parts.join(" ")}</span>;
}

// ------------------------------------------------------- empty / error / load

export function EmptyState({
  message,
  actionLabel,
  actionTo,
  onAction,
  icon = "template",
}: {
  message: string;
  actionLabel?: string;
  actionTo?: string;
  onAction?: () => void;
  icon?: IconName;
}) {
  /*
    `min-h-64` 이 없으면 넓은 칸(예: /market 오른쪽 컬럼)에서 이 상태가 텍스트 한 줄로
    납작해져서, 1440px 화면의 절반이 아무것도 없는 여백이 돼요. 빈 상태도 자리를 차지해야
    "여기가 무엇을 담는 칸인지" 가 보입니다.
  */
  return (
    <div className="flex min-h-64 flex-col items-center justify-center gap-4 px-6 py-14 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-fill-alternative text-label-assistive">
        <Icon name={icon} size={24} />
      </span>
      <p className="max-w-md text-body-2 text-label-alternative">{message}</p>
      {actionLabel && actionTo ? (
        <Link to={actionTo}>
          <Button variant="solid" size="medium">
            {actionLabel}
          </Button>
        </Link>
      ) : null}
      {actionLabel && !actionTo && onAction ? (
        <Button variant="solid" size="medium" onClick={onAction}>
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const action = errorAction(error);
  // 감싸는 `px-1 py-2` 를 뺐어요 — 카드 안에 들어가면 그 1px 만큼 카드 패딩과 어긋나서
  // 분홍 블록이 삐뚤게 놓인 것처럼 보였습니다. 여백은 담는 쪽(`Section`)이 이미 줘요.
  return (
    <Alert
      tone="negative"
      title="불러오지 못했어요"
      action={
        action?.to ? (
          <Link to={action.to}>
            <TextButton color="primary" size="small">
              {action.label}
            </TextButton>
          </Link>
        ) : onRetry ? (
          <TextButton color="primary" size="small" onClick={onRetry}>
            다시 시도하기
          </TextButton>
        ) : undefined
      }
    >
      {errorMessage(error)}
    </Alert>
  );
}

/**
 * 도착할 내용과 같은 모양의 자리표시 블록.
 *
 * `aria-hidden` 이에요 — 스크린리더에는 이 줄무늬가 아무 의미도 없고, 대신
 * 기다린다는 사실은 담는 쪽(`LoadingBlock`)의 문구가 라이브 리전으로 알려요.
 * 둘 다 읽히면 "블록 블록 블록 불러오고 있어요" 가 됩니다.
 */
export function Skeleton({ className }: { className: string }) {
  return <div aria-hidden className={`apple-skeleton ${className}`} />;
}

/**
 * 기다리는 칸.
 *
 * 원형 스피너는 "기다려" 만 말하고 **무엇을** 기다리는지 말하지 않아요. 그래서
 * 기본값을 도착할 목록의 모양(`rows`)으로 뒀습니다 — 값이 도착할 때 칸 높이가
 * 그대로라 화면이 안 튑니다. 모양을 모르는 자리(라우트 청크 로딩처럼 무엇이 올지
 * 정해지지 않은 곳)만 `spinner` 를 씁니다.
 *
 * 높이는 예전과 같은 `min-h-64` 예요 — 로딩·빈 상태·본문이 같은 칸을 번갈아
 * 차지하므로, 셋 중 하나만 높이가 다르면 데이터가 도착할 때마다 화면이 튑니다.
 */
export function LoadingBlock({
  label = "불러오고 있어요",
  variant = "rows",
}: {
  label?: string;
  variant?: "rows" | "tiles" | "spinner";
}) {
  if (variant === "spinner") {
    return (
      <div className="flex min-h-64 flex-col items-center justify-center gap-3 py-14">
        <Spinner size={28} />
        <p className="text-label-2 text-label-assistive">{label}</p>
      </div>
    );
  }

  return (
    <div className="min-h-64 py-2">
      {/* 문구는 남겨 두되 눈에서는 물러나요 — 스켈레톤이 이미 "오는 중" 을
          말하고 있어서, 같은 말을 크게 두 번 하면 칸이 시끄러워집니다.
          `sr-only` 로 숨기지 않는 이유: 네트워크가 느릴 때 이 한 줄이 "빈
          목록" 과 "아직 안 온 목록" 을 가르는 유일한 문장이에요. */}
      <p className="mb-3 text-label-2 text-label-assistive">{label}</p>

      {variant === "tiles" ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="flex flex-col gap-2 rounded-lg border border-line-alternative p-4"
            >
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-6 w-32" />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {/* 줄 너비를 조금씩 다르게 둬요. 다 같은 길이면 표가 아니라 진행
              막대처럼 보이고, 도착한 목록과 모양이 달라서 튑니다. */}
          {["w-2/5", "w-3/5", "w-1/2", "w-4/6", "w-1/3"].map((w) => (
            <div
              key={w}
              className="flex items-center justify-between gap-4 border-b border-line-alternative py-3 last:border-b-0"
            >
              <Skeleton className={`h-4 ${w}`} />
              <Skeleton className="h-4 w-16 shrink-0" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Renders one of loading / error / empty / content. Every list in the product
 * goes through here so no screen can forget one of the four states.
 */
export function QueryBoundary({
  isLoading,
  error,
  isEmpty,
  empty,
  onRetry,
  loading = "rows",
  children,
}: {
  isLoading: boolean;
  error: unknown;
  isEmpty?: boolean;
  empty?: ReactNode;
  onRetry?: () => void;
  /** 도착할 내용의 모양. 목록이면 `rows`, KPI 카드 줄이면 `tiles`. */
  loading?: "rows" | "tiles" | "spinner";
  children: ReactNode;
}) {
  if (isLoading) return <LoadingBlock variant={loading} />;
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  if (isEmpty) return <>{empty}</>;
  return <>{children}</>;
}

// ------------------------------------------------------------- disclaimers

export function PerformanceDisclaimer() {
  return (
    <p className="flex items-start gap-2 rounded-lg border border-line-alternative px-4 py-3 text-caption-1 text-label-assistive">
      <span className="mt-wds-1 shrink-0">
        <Icon name="circleInfo" size={14} />
      </span>
      {PERFORMANCE_DISCLAIMER}
    </p>
  );
}

// --------------------------------------------------------------- small utils

export function CopyButton({ value, label = "복사하기" }: { value: string; label?: string }) {
  return (
    <Button
      variant="outlined"
      size="small"
      iconLeft="document"
      onClick={() => {
        void navigator.clipboard
          .writeText(value)
          .then(() => toast.success("복사했어요"))
          .catch(() => toast.error("복사하지 못했어요. 직접 선택해서 복사해 주세요."));
      }}
    >
      {label}
    </Button>
  );
}

/** PRD §8-13 — there is no WebSocket, so say when the number was read. */
export function FreshnessStamp({ asOf, seconds }: { asOf?: string | null; seconds?: number }) {
  const text =
    seconds !== undefined
      ? `${seconds}초 전 기준`
      : asOf
        ? `${new Date(asOf).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })} 기준`
        : "갱신 시각 확인 중";
  return (
    <span className="tabular inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-caption-1 text-label-assistive">
      <Icon name="clock" size={12} />
      {text}
    </span>
  );
}

export function ProgressBar({
  ratio,
  tone = "primary",
}: {
  ratio: number;
  tone?: "primary" | "positive";
}) {
  const pct = Math.round(Math.min(1, Math.max(0, ratio)) * 100);
  const fill = tone === "positive" ? "bg-status-positive" : "bg-primary";
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-fill-alternative">
      <div
        className={`h-full rounded-full ${fill} transition-all duration-slow ease-standard`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function LabelValue({ label, value }: { label: string; value: ReactNode }) {
  return (
    // 라벨은 가장 가볍게, 값은 가장 무겁게. 값을 오른쪽으로 붙여야 여러 줄이 쌓였을 때
    // 눈이 한 축을 따라 내려가요.
    <div className="flex items-start justify-between gap-4 py-2.5">
      <span className="shrink-0 text-label-2 text-label-alternative">{label}</span>
      <span className="min-w-0 text-right text-label-1 text-label-strong">{value}</span>
    </div>
  );
}

/**
 * 종목 한 칸. **코드가 아니라 이름이 먼저 보여야 해요** — 운영자는 `005930` 이 아니라
 * 삼성전자로 종목을 기억합니다.
 *
 * 이름은 종목 사전에서 옵니다(`useStockNames`). 사전에 아직 없는 종목은 이름을 지어내지
 * 않고 코드를 그대로 보여줘요 — 전체 종목 나열 API 가 없어서 사전은 랭킹으로만 쌓입니다.
 *
 * 코드를 지우지는 않습니다. 주문 대조·격리 해제처럼 코드로 확인해야 하는 작업이 있어서,
 * 이름 아래에 작게 남겨요. `inline` 은 한 줄만 쓸 수 있는 자리(칩·타임라인)입니다.
 */
export function StockName({ symbol, inline = false }: { symbol: string; inline?: boolean }) {
  const names = useStockNames();
  const name = names[symbol];
  if (!name || name === symbol) return <span className="tabular">{symbol}</span>;
  if (inline) return <span>{name}</span>;
  return (
    <span className="flex flex-col">
      <span className="text-label-1 text-label-normal">{name}</span>
      <span className="tabular text-caption-1 text-label-assistive">{symbol}</span>
    </span>
  );
}

type TagTone = "neutral" | "primary" | "positive" | "negative" | "cautionary" | "violet";

/*
  약한 primary 태그의 글자만 한 단 눌러요(`--primary-strong`). vendored `Tag` 는 그 글자를
  `--primary-normal` 로 칠하는데, 브랜드 블루는 흰 바탕에서도 4.68:1 이라 어떤 틴트를 깔아도
  4.5 아래로 떨어집니다(`styles/apple/contrast.test.ts`). 다크에서는 `primary-strong` 이 더
  **밝은** 쪽이라 같은 선언이 두 테마 모두에서 대비를 올려요.
*/
const PRIMARY_WEAK_INK = { color: "var(--primary-strong)" } as const;

/**
 * 상태 태그 — tone 맵(`lib/labels.ts`)에서 색을 받는 자리는 전부 이걸 써요.
 *
 * `Tag` 와 props 가 같고, 약한 primary 일 때만 글자색을 바로잡습니다. 프리미티브는
 * vendored 라 고치지 않고(`CLAUDE.md`), 호출하는 쪽 한 곳에서만 보정해요.
 */
export function StatusTag({
  color = "neutral",
  variant = "weak",
  size = "small",
  children,
}: {
  color?: TagTone;
  variant?: "weak" | "solid" | "outline";
  size?: "medium" | "small";
  children: ReactNode;
}) {
  return (
    <Tag
      color={color}
      variant={variant}
      size={size}
      style={color === "primary" && variant === "weak" ? PRIMARY_WEAK_INK : undefined}
    >
      {children}
    </Tag>
  );
}

export function InlineTag({
  tone,
  children,
}: {
  tone: TagTone;
  children: ReactNode;
}) {
  return (
    <StatusTag color={tone} variant="weak" size="small">
      {children}
    </StatusTag>
  );
}

// ---------------------------------------------------------------- data table

export interface Column<T> {
  key: string;
  header: string;
  /** Numeric columns get tabular figures and right alignment. */
  numeric?: boolean;
  render: (row: T) => ReactNode;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowLabel,
  stackOnMobile = false,
}: {
  columns: Array<Column<T>>;
  rows: T[];
  rowKey: (row: T) => string;
  /** 주면 행 전체가 버튼처럼 동작해요. 안 주면 예전처럼 정적인 표예요. */
  onRowClick?: (row: T) => void;
  /** 클릭 가능한 행의 스크린리더 라벨. `onRowClick` 이 있으면 반드시 함께 줘요. */
  rowLabel?: (row: T) => string;
  /**
   * 폰(640px 미만)에서 행을 카드처럼 세로로 쌓아요 — 첫 칸이 제목, 나머지는 "헤더: 값" 줄.
   * 열이 많은 표는 가로 스크롤 뒤에 핵심 열(평가손익 등)이 숨어서, 폰에서는 읽을 수 없었어요
   * (QA 390px 실측). DOM 은 그대로고 표시만 바뀝니다(`styles/index.css` 의 `.data-table-stack`).
   */
  stackOnMobile?: boolean;
}) {
  return (
    <div className={`scroll-x-shadow -mx-1 ${stackOnMobile ? "data-table-stack" : ""}`}>
      <table className="w-full min-w-max border-collapse">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={`whitespace-nowrap border-b border-line-normal px-3 pb-2 text-caption-1 font-semibold text-label-neutral ${
                  column.numeric ? "text-right" : "text-left"
                }`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            /*
              행 클릭은 `<tr>` 에 건다. 셀마다 버튼을 넣으면 어느 칸을 눌러야 열리는지
              사용자가 알 수 없고, 표를 통째로 `<button>` 으로 감쌀 수도 없다(테이블 구조가
              깨진다). 키보드 접근은 `tabIndex` + Enter/Space 로 직접 채운다.
            */
            <tr
              key={rowKey(row)}
              /*
                hover 는 클릭 가능 여부와 무관하게 답니다 — 긴 목록에서 눈이 행을 놓치지
                않게 하는 장치예요. "누를 수 있다" 는 신호는 `cursor-pointer` 가 맡습니다.
              */
              /*
                `apple-pressable-row` 는 **누르는 순간**(pointer-down) 바탕이
                눌려요. hover 만 있으면 마우스에서는 커서를 올려둔 상태와
                누른 상태가 같아 보이고, 터치에서는 hover 가 아예 없어서
                누른 티가 안 납니다 — 느린 응답을 기다리는 동안 사용자가
                같은 행을 두 번 누르는 자리가 여기였어요.

                행은 `scale` 로 눌리지 않습니다(`apple-pressable` 이 아니라
                `-row` 인 이유) — 표에서 한 줄만 작아지면 옆 줄과 경계가
                어긋나 보여요.
              */
              className={`border-b border-line-alternative transition-colors duration-fast ease-standard last:border-b-0 hover:bg-background-alternative ${
                onRowClick ? "apple-pressable-row cursor-pointer" : ""
              }`}
              {...(onRowClick
                ? {
                    role: "button",
                    tabIndex: 0,
                    "aria-label": rowLabel?.(row),
                    onClick: () => onRowClick(row),
                    onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      // Space 는 기본 동작이 스크롤이라 막아야 표가 튀지 않는다.
                      event.preventDefault();
                      onRowClick(row);
                    },
                  }
                : {})}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  data-label={column.header}
                  className={`whitespace-nowrap px-3 py-3.5 text-label-1 text-label-normal ${
                    column.numeric ? "tabular text-right" : "text-left"
                  }`}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
